import APIWrapper from '../utils/graphqlwrapper';
import logger from '../utils/logger';

class SubscriptionService {
  constructor() {
    this.activeSubscriptions = new Map();
  }

  async startSumBySourceSubscription(executionId, clientId) {
    /**
     * Starts a subscription for Sum By Source export results
     * @param {string} executionId The ID of the Sum By Source execution
     * @param {string} clientId The client ID associated with the execution
     * @returns {Object} The subscription object
     */
    const subscriptionKey = `sbs-${executionId}`;

    // Don't create duplicate subscriptions
    if (this.activeSubscriptions.has(subscriptionKey)) {
      logger.info('Subscription already active for execution:', executionId);
      return this.activeSubscriptions.get(subscriptionKey);
    }

    try {
      logger.info('Starting SBS subscription for execution:', executionId);

      const subscriptionObservable = await APIWrapper.subscribeApi(
        { subscription: 'onUpdateSumBySourceExportResults' },
        false,
        {
          filter: { id: { eq: executionId } },
        },
      );

      const subscription = subscriptionObservable.subscribe({
        next: (response) => {
          logger.info('SBS subscription update received for:', executionId);

          try {
            let responseData = null;

            // aws-amplify v6 returns response.data
            if (response && response.data) {
              responseData = response.data.onUpdateSumBySourceExportResults;
            }

            if (responseData) {
              logger.info('SBS job status update:', {
                id: responseData.id,
                status: responseData.status,
                client_id: responseData.client_id,
              });

              // Emit custom event for snackbar notification
              const { status } = responseData;
              window.dispatchEvent(new CustomEvent('sbsStatusUpdate', {
                detail: {
                  executionId,
                  clientId,
                  status,
                  data: responseData,
                },
              }));

              // Auto-unsubscribe on completion
              if (status === 'Success' || status === 'Failed') {
                logger.info('SBS job completed, unsubscribing...');
                this.unsubscribe(subscriptionKey);
              }
            }
          } catch (err) {
            logger.error('Error processing SBS subscription response:', err.message);
          }
        },
        error: (error) => {
          logger.error('SBS subscription error:', error);
          this.unsubscribe(subscriptionKey);
        },
      });

      // Store the subscription
      const subscriptionInfo = {
        subscription,
        executionId,
        clientId,
        startTime: Date.now(),
      };

      this.activeSubscriptions.set(subscriptionKey, subscriptionInfo);
      logger.info('SBS subscription started and stored:', subscriptionKey);

      return subscriptionInfo;
    } catch (err) {
      logger.error('Failed to start SBS subscription:', err);
      throw err;
    }
  }

  unsubscribe(subscriptionKey) {
    /**
     * Unsubscribes from a given subscription
     * @param {string} subscriptionKey The key of the subscription to unsubscribe from
     */
    const subscriptionInfo = this.activeSubscriptions.get(subscriptionKey);
    if (subscriptionInfo) {
      logger.info('Unsubscribing from:', subscriptionKey);
      subscriptionInfo.subscription.unsubscribe();
      this.activeSubscriptions.delete(subscriptionKey);
      logger.info('Successfully unsubscribed from:', subscriptionKey);
    } else {
      logger.warn('No active subscription found for:', subscriptionKey);
    }
  }

  unsubscribeAll() {
    /**
     * Unsubscribes from all active subscriptions
     */
    logger.info('Unsubscribing from all active subscriptions...');
    this.activeSubscriptions.forEach((_, key) => {
      this.unsubscribe(key);
    });
  }

  getActiveSubscriptions() {
    /**
     * Returns a list of active subscriptions
     * @returns {Array} List of active subscription info
     */
    return Array.from(this.activeSubscriptions.entries()).map(([key, info]) => ({
      key,
      executionId: info.executionId,
      clientId: info.clientId,
      startTime: info.startTime,
    }));
  }

  isSubscriptionActive(executionId) {
    /**
     * Checks if a subscription is active for a given execution ID
     * @param {string} executionId The execution ID to check
     * @returns {boolean} True if active, false otherwise
     */
    return this.activeSubscriptions.has(`sbs-${executionId}`);
  }
}

// Create an instance
const subscriptionService = new SubscriptionService();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    subscriptionService.unsubscribeAll();
  });
}

export default subscriptionService;
